import { safeNumber } from './format';

const finiteNumbers = (arr) =>
  (Array.isArray(arr) ? arr : []).filter((x) => typeof x === 'number' && Number.isFinite(x));

/**
 * Arithmetic mean of finite numbers in arr. Returns NaN for empty input.
 * @param {number[]} arr
 * @returns {number}
 */
export function mean(arr) {
  const ns = finiteNumbers(arr);
  if (ns.length === 0) return NaN;
  return ns.reduce((s, x) => s + x, 0) / ns.length;
}

/**
 * Population standard deviation. Returns NaN for empty input, 0 for length 1.
 * @param {number[]} arr
 * @returns {number}
 */
export function stdDev(arr) {
  const ns = finiteNumbers(arr);
  if (ns.length === 0) return NaN;
  if (ns.length === 1) return 0;
  const m = mean(ns);
  const variance = ns.reduce((s, x) => s + (x - m) ** 2, 0) / ns.length;
  return Math.sqrt(variance);
}

/**
 * Trailing rolling mean with the given window. Output has the same length as arr;
 * leading positions where window is unsatisfied use the partial mean of values seen so far.
 * Returns [] for non-array input.
 * @param {number[]} arr
 * @param {number} window
 * @returns {number[]}
 */
export function rollingMean(arr, window) {
  if (!Array.isArray(arr) || arr.length === 0) return [];
  const w = Math.max(1, Math.floor(safeNumber(window, 1)));
  const out = new Array(arr.length);
  for (let i = 0; i < arr.length; i++) {
    const start = Math.max(0, i - w + 1);
    const slice = arr.slice(start, i + 1).filter((x) => Number.isFinite(x));
    out[i] = slice.length === 0 ? NaN : slice.reduce((s, x) => s + x, 0) / slice.length;
  }
  return out;
}

/**
 * Exponentially weighted moving average with smoothing factor alpha in (0, 1].
 * @param {number[]} arr
 * @param {number} [alpha=0.3]
 * @returns {number[]}
 */
export function ewma(arr, alpha = 0.3) {
  if (!Array.isArray(arr) || arr.length === 0) return [];
  const a = Math.min(1, Math.max(Number.EPSILON, safeNumber(alpha, 0.3)));
  const out = new Array(arr.length);
  let prev = NaN;
  for (let i = 0; i < arr.length; i++) {
    const x = arr[i];
    if (!Number.isFinite(x)) {
      out[i] = prev;
      continue;
    }
    prev = Number.isFinite(prev) ? a * x + (1 - a) * prev : x;
    out[i] = prev;
  }
  return out;
}

/**
 * Quantile q in [0, 1] using linear interpolation between order statistics.
 * Returns NaN for empty input.
 * @param {number[]} arr
 * @param {number} q
 * @returns {number}
 */
export function quantile(arr, q) {
  const ns = finiteNumbers(arr).slice().sort((a, b) => a - b);
  if (ns.length === 0) return NaN;
  const qc = Math.min(1, Math.max(0, safeNumber(q, 0.5)));
  const pos = (ns.length - 1) * qc;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  if (lo === hi) return ns[lo];
  return ns[lo] + (pos - lo) * (ns[hi] - ns[lo]);
}
